import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CaseInterpretationPanel } from './CaseInterpretationPanel';
import { CaseTreatmentPlanPanel } from './CaseTreatmentPlanPanel';

// Phase 1 (intake) + Phase 2B (AI interpretation) of practitioner portal.
// Plaskett-style case-taking (F08 B Data Collection structure) plus a separate
// tab for the AI-generated interpretation grounded in the Plaskett corpus.

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3002';

interface CaseSummary {
  id: string;
  clientUserId: string;
  clientName?: string;
  clientEmail?: string;
  caseStatus: 'draft' | 'active' | 'archived';
  caseDate: string;
  caseTitle: string | null;
  patientGoals: string | null;
  createdAt: string;
  updatedAt: string;
}

interface CaseData {
  preliminary?: {
    physicalAppearance?: string;
    healthOrientation?: string;
    patientUnderstandingWNT?: string;
    patientViewOfDisease?: string;
  };
  medicalLifeHistory?: {
    pastYear?: string;
    babyhood?: string;
    preschoolYears?: string;
    schoolTeenageYears?: string;
    adultHistory?: string;
    pregnancyChildbirth?: string;
  };
  familyHistory?: {
    parents?: string;
    grandparents?: string;
    siblings?: string;
    extended?: string;
  };
  diet?: {
    presentDiet?: string;
    pastDietaryHistory?: string;
  };
  lifestyleFactors?: string;
  bodyFunctions?: {
    bowel?: string;
    waterworks?: string;
    digestion?: string;
    appetite?: string;
    respiratory?: string;
    menstrual?: string;
  };
}

interface CaseFull extends CaseSummary {
  caseData: CaseData;
  patientResponsibilityNotes: string | null;
  practitionerNotes: string | null;
}

interface CasesPageProps {
  user: any;
  coachMode?: boolean;
  clientId?: string;
  clientName?: string;
}

const today = () => new Date().toISOString().split('T')[0];

async function apiFetch(path: string, options: RequestInit = {}) {
  const token = localStorage.getItem('auth_token');
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {})
    }
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`${res.status}: ${text || res.statusText}`);
  }
  return res.json();
}

export const CasesPage: React.FC<CasesPageProps> = ({ user, coachMode, clientId, clientName }) => {
  const effectiveClientId: string | undefined = clientId || user?.id;
  const effectiveClientName: string | undefined = clientName || user?.full_name || user?.email;
  const clientGender: string | undefined = user?.gender;

  const [cases, setCases] = useState<CaseSummary[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedCase, setSelectedCase] = useState<CaseFull | null>(null);
  const [loadingList, setLoadingList] = useState(false);
  const [loadingCase, setLoadingCase] = useState(false);
  const [creating, setCreating] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [editorTab, setEditorTab] = useState<'intake' | 'interpretation' | 'treatment'>('intake');

  const loadList = useCallback(async () => {
    if (!effectiveClientId) return;
    setLoadingList(true);
    try {
      const data = await apiFetch(`/api/practitioner-cases?clientUserId=${encodeURIComponent(effectiveClientId)}`);
      setCases(data.cases || []);
      setError(null);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoadingList(false);
    }
  }, [effectiveClientId]);

  useEffect(() => { loadList(); }, [loadList]);

  const loadCase = useCallback(async (id: string) => {
    setLoadingCase(true);
    try {
      const data = await apiFetch(`/api/practitioner-cases/${id}`);
      setSelectedCase(data.case);
      setError(null);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoadingCase(false);
    }
  }, []);

  useEffect(() => {
    if (selectedId) loadCase(selectedId);
    else setSelectedCase(null);
    setEditorTab('intake'); // reset tab when switching cases
  }, [selectedId, loadCase]);

  const handleCreate = async () => {
    if (!effectiveClientId) return;
    setCreating(true);
    try {
      const data = await apiFetch('/api/practitioner-cases', {
        method: 'POST',
        body: JSON.stringify({
          clientUserId: effectiveClientId,
          caseDate: today(),
          caseTitle: `Consultation — ${today()}`,
          caseData: {}
        })
      });
      await loadList();
      setSelectedId(data.case.id);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setCreating(false);
    }
  };

  // Auto-save a partial update. Used by every textarea onBlur.
  const saveDraft = useCallback(async (patch: Record<string, any>) => {
    if (!selectedCase) return;
    setSaveStatus('saving');
    try {
      const data = await apiFetch(`/api/practitioner-cases/${selectedCase.id}`, {
        method: 'PATCH',
        body: JSON.stringify(patch)
      });
      setSelectedCase(data.case);
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 1500);
      await loadList();
    } catch (e: any) {
      setError(e.message);
      setSaveStatus('error');
    }
  }, [selectedCase, loadList]);

  if (!effectiveClientId) {
    return (
      <div className="p-8 text-center text-gray-600">
        Select a client from the sidebar to view or create their cases.
      </div>
    );
  }

  return (
    <div className="flex h-full bg-gray-50">
      {/* Case list (left) */}
      <aside className="w-72 border-r border-gray-200 bg-white flex flex-col">
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-lg font-semibold text-gray-900">Cases</h2>
            <button
              onClick={handleCreate}
              disabled={creating}
              className="text-sm bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded disabled:opacity-50"
            >
              {creating ? 'Creating…' : '+ New'}
            </button>
          </div>
          <p className="text-xs text-gray-500">{effectiveClientName}</p>
        </div>
        <div className="flex-1 overflow-y-auto">
          {loadingList && <div className="p-4 text-sm text-gray-500">Loading…</div>}
          {!loadingList && cases.length === 0 && (
            <div className="p-4 text-sm text-gray-500">No cases yet. Click <strong>+ New</strong> to start one.</div>
          )}
          {cases.map(c => (
            <button
              key={c.id}
              onClick={() => setSelectedId(c.id)}
              className={`w-full text-left p-3 border-b border-gray-100 hover:bg-gray-50 transition-colors ${
                selectedId === c.id ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
              }`}
            >
              <div className="text-sm font-medium text-gray-900 truncate">
                {c.caseTitle || `Consultation — ${c.caseDate}`}
              </div>
              <div className="text-xs text-gray-500 mt-1 flex gap-2">
                <span>{c.caseDate}</span>
                <span className={`px-1.5 py-0.5 rounded text-[10px] uppercase ${
                  c.caseStatus === 'draft' ? 'bg-yellow-100 text-yellow-700'
                  : c.caseStatus === 'active' ? 'bg-green-100 text-green-700'
                  : 'bg-gray-100 text-gray-600'
                }`}>{c.caseStatus}</span>
              </div>
            </button>
          ))}
        </div>
      </aside>

      {/* Editor (right) */}
      <main className="flex-1 overflow-y-auto flex flex-col">
        {error && (
          <div className="p-3 m-4 bg-red-50 border border-red-200 text-red-800 text-sm rounded">{error}</div>
        )}
        {!selectedId && !loadingCase && (
          <div className="p-12 text-center text-gray-500">
            <div className="text-5xl mb-4">📋</div>
            <p>Select a case from the list, or start a new one.</p>
          </div>
        )}
        {loadingCase && <div className="p-8 text-gray-500">Loading case…</div>}
        {selectedCase && (
          <>
            {/* Tab strip */}
            <div className="bg-white border-b border-gray-200 px-6 pt-4">
              <div className="flex gap-2">
                <button
                  onClick={() => setEditorTab('intake')}
                  className={`px-4 py-2 text-sm font-medium rounded-t-md border-b-2 transition-colors ${
                    editorTab === 'intake'
                      ? 'border-blue-600 text-blue-700 bg-blue-50'
                      : 'border-transparent text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                  }`}
                >
                  📋 Intake
                </button>
                <button
                  onClick={() => setEditorTab('interpretation')}
                  className={`px-4 py-2 text-sm font-medium rounded-t-md border-b-2 transition-colors ${
                    editorTab === 'interpretation'
                      ? 'border-purple-600 text-purple-700 bg-purple-50'
                      : 'border-transparent text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                  }`}
                >
                  🧪 Interpretation
                </button>
                <button
                  onClick={() => setEditorTab('treatment')}
                  className={`px-4 py-2 text-sm font-medium rounded-t-md border-b-2 transition-colors ${
                    editorTab === 'treatment'
                      ? 'border-emerald-600 text-emerald-700 bg-emerald-50'
                      : 'border-transparent text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                  }`}
                >
                  💊 Treatment
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto">
              {editorTab === 'intake' && (
                <CaseEditor
                  theCase={selectedCase}
                  clientGender={clientGender}
                  saveStatus={saveStatus}
                  onSave={saveDraft}
                />
              )}
              {editorTab === 'interpretation' && (
                <CaseInterpretationPanel caseId={selectedCase.id} />
              )}
              {editorTab === 'treatment' && (
                <CaseTreatmentPlanPanel caseId={selectedCase.id} />
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
};

// ---------- Editor ----------

interface CaseEditorProps {
  theCase: CaseFull;
  clientGender?: string;
  saveStatus: 'idle' | 'saving' | 'saved' | 'error';
  onSave: (patch: Record<string, any>) => void;
}

const CaseEditor: React.FC<CaseEditorProps> = ({ theCase, clientGender, saveStatus, onSave }) => {
  // Local mirror of editable fields so typing feels instant.
  // We commit to the server on textarea blur via the onSave prop.
  const [title, setTitle] = useState(theCase.caseTitle || '');
  const [date, setDate] = useState(theCase.caseDate || today());
  const [status, setStatus] = useState(theCase.caseStatus);
  const [goals, setGoals] = useState(theCase.patientGoals || '');
  const [responsibility, setResponsibility] = useState(theCase.patientResponsibilityNotes || '');
  const [practitionerNotes, setPractitionerNotes] = useState(theCase.practitionerNotes || '');
  const [data, setData] = useState<CaseData>(theCase.caseData || {});

  // When a new case is loaded from the parent, reset local mirror.
  const lastId = useRef(theCase.id);
  useEffect(() => {
    if (lastId.current !== theCase.id) {
      setTitle(theCase.caseTitle || '');
      setDate(theCase.caseDate || today());
      setStatus(theCase.caseStatus);
      setGoals(theCase.patientGoals || '');
      setResponsibility(theCase.patientResponsibilityNotes || '');
      setPractitionerNotes(theCase.practitionerNotes || '');
      setData(theCase.caseData || {});
      lastId.current = theCase.id;
    }
  }, [theCase]);

  const updateSection = <S extends keyof CaseData>(section: S, key: string, value: string) => {
    setData(prev => ({
      ...prev,
      [section]: {
        ...(prev[section] as any || {}),
        [key]: value
      }
    }));
  };

  const flushSection = (section: keyof CaseData) => {
    onSave({ caseData: { [section]: data[section] || {} } });
  };

  const flushLifestyle = () => onSave({ caseData: { lifestyleFactors: data.lifestyleFactors || '' } });

  const showMenstrual = useMemo(() => {
    if (!clientGender) return true;
    return clientGender.toLowerCase() === 'female';
  }, [clientGender]);

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
        <div className="flex items-start justify-between gap-4 mb-4">
          <input
            type="text"
            value={title}
            placeholder="Case title"
            onChange={e => setTitle(e.target.value)}
            onBlur={() => onSave({ caseTitle: title })}
            className="text-2xl font-semibold text-gray-900 border-b border-transparent focus:border-blue-500 outline-none flex-1 bg-transparent"
          />
          <SaveIndicator status={saveStatus} />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <label className="block">
            <span className="text-xs text-gray-600 uppercase tracking-wide">Consultation Date</span>
            <input
              type="date"
              value={date}
              onChange={e => setDate(e.target.value)}
              onBlur={() => onSave({ caseDate: date })}
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            />
          </label>
          <label className="block">
            <span className="text-xs text-gray-600 uppercase tracking-wide">Status</span>
            <select
              value={status}
              onChange={e => {
                const v = e.target.value as CaseFull['caseStatus'];
                setStatus(v);
                onSave({ caseStatus: v });
              }}
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            >
              <option value="draft">Draft</option>
              <option value="active">Active</option>
              <option value="archived">Archived</option>
            </select>
          </label>
        </div>
      </div>

      <Section title="Preliminary Details" hint="From F08 B §2.1 — the administrative and orienting picture.">
        <Field label="Physical Appearance" hint="Hair, eyes, skin, body shape — anything that may be diagnostic.">
          <Textarea
            value={data.preliminary?.physicalAppearance || ''}
            onChange={v => updateSection('preliminary', 'physicalAppearance', v)}
            onBlur={() => flushSection('preliminary')}
          />
        </Field>
        <Field label="Health Orientation" hint="Patient's stance toward alternative vs orthodox medicine — likely acceptance of treatment.">
          <Textarea
            value={data.preliminary?.healthOrientation || ''}
            onChange={v => updateSection('preliminary', 'healthOrientation', v)}
            onBlur={() => flushSection('preliminary')}
          />
        </Field>
        <Field label="Patient's Understanding of Wholistic Nutrition" hint="What they already grasp about the approach.">
          <Textarea
            value={data.preliminary?.patientUnderstandingWNT || ''}
            onChange={v => updateSection('preliminary', 'patientUnderstandingWNT', v)}
            onBlur={() => flushSection('preliminary')}
          />
        </Field>
        <Field label="Patient's View of Their Disease" hint="How they describe and understand what is wrong.">
          <Textarea
            value={data.preliminary?.patientViewOfDisease || ''}
            onChange={v => updateSection('preliminary', 'patientViewOfDisease', v)}
            onBlur={() => flushSection('preliminary')}
          />
        </Field>
      </Section>

      <Section title="Medical Life History" hint="Under each lifestage record accidents/injuries, illnesses and medical treatment given (drugs, operations, alternative).">
        <Field label="The Past Year" hint="Current symptoms and drugs being used.">
          <Textarea
            value={data.medicalLifeHistory?.pastYear || ''}
            onChange={v => updateSection('medicalLifeHistory', 'pastYear', v)}
            onBlur={() => flushSection('medicalLifeHistory')}
            rows={6}
          />
        </Field>
        <Field label="Babyhood">
          <Textarea
            value={data.medicalLifeHistory?.babyhood || ''}
            onChange={v => updateSection('medicalLifeHistory', 'babyhood', v)}
            onBlur={() => flushSection('medicalLifeHistory')}
          />
        </Field>
        <Field label="Pre-school Years">
          <Textarea
            value={data.medicalLifeHistory?.preschoolYears || ''}
            onChange={v => updateSection('medicalLifeHistory', 'preschoolYears', v)}
            onBlur={() => flushSection('medicalLifeHistory')}
          />
        </Field>
        <Field label="School and Teenage Years">
          <Textarea
            value={data.medicalLifeHistory?.schoolTeenageYears || ''}
            onChange={v => updateSection('medicalLifeHistory', 'schoolTeenageYears', v)}
            onBlur={() => flushSection('medicalLifeHistory')}
          />
        </Field>
        <Field label="Adult History" hint="From ~18 to one year ago, or up to the start of the present illness.">
          <Textarea
            value={data.medicalLifeHistory?.adultHistory || ''}
            onChange={v => updateSection('medicalLifeHistory', 'adultHistory', v)}
            onBlur={() => flushSection('medicalLifeHistory')}
            rows={6}
          />
        </Field>
        <Field label="Pregnancy and Childbirth" hint="If relevant — can be merged into adult history.">
          <Textarea
            value={data.medicalLifeHistory?.pregnancyChildbirth || ''}
            onChange={v => updateSection('medicalLifeHistory', 'pregnancyChildbirth', v)}
            onBlur={() => flushSection('medicalLifeHistory')}
          />
        </Field>
      </Section>

      <Section title="Family History" hint="Note current and past health of relatives. Where a relative has died, the cause is worth knowing as a clue to family weaknesses.">
        <Field label="Parents">
          <Textarea
            value={data.familyHistory?.parents || ''}
            onChange={v => updateSection('familyHistory', 'parents', v)}
            onBlur={() => flushSection('familyHistory')}
          />
        </Field>
        <Field label="Grandparents">
          <Textarea
            value={data.familyHistory?.grandparents || ''}
            onChange={v => updateSection('familyHistory', 'grandparents', v)}
            onBlur={() => flushSection('familyHistory')}
          />
        </Field>
        <Field label="Brothers and Sisters">
          <Textarea
            value={data.familyHistory?.siblings || ''}
            onChange={v => updateSection('familyHistory', 'siblings', v)}
            onBlur={() => flushSection('familyHistory')}
          />
        </Field>
        <Field label="Aunts, Uncles, Cousins" hint="Where relevant.">
          <Textarea
            value={data.familyHistory?.extended || ''}
            onChange={v => updateSection('familyHistory', 'extended', v)}
            onBlur={() => flushSection('familyHistory')}
          />
        </Field>
      </Section>

      <Section title="Diet">
        <Field label="Present Diet" hint="Record a typical day's eating and drinking in full.">
          <Textarea
            value={data.diet?.presentDiet || ''}
            onChange={v => updateSection('diet', 'presentDiet', v)}
            onBlur={() => flushSection('diet')}
            rows={8}
          />
        </Field>
        <Field label="Past Dietary History" hint="Past phases where diet was markedly different from today.">
          <Textarea
            value={data.diet?.pastDietaryHistory || ''}
            onChange={v => updateSection('diet', 'pastDietaryHistory', v)}
            onBlur={() => flushSection('diet')}
          />
        </Field>
      </Section>

      <Section title="Other Lifestyle Factors" hint="Alcohol, smoking, non-medical addictive drugs, chemical exposure (pesticides, household chemicals, occupational toxins).">
        <Field label="Lifestyle">
          <Textarea
            value={data.lifestyleFactors || ''}
            onChange={v => setData(p => ({ ...p, lifestyleFactors: v }))}
            onBlur={flushLifestyle}
            rows={6}
          />
        </Field>
      </Section>

      <Section title="Body Functions" hint="Bowel, waterworks, digestion, appetite, respiratory. For females, always ask about menstrual cycle abnormalities. If menopausal, enquire about menstrual cycle history.">
        <Field label="Bowel Function">
          <Textarea
            value={data.bodyFunctions?.bowel || ''}
            onChange={v => updateSection('bodyFunctions', 'bowel', v)}
            onBlur={() => flushSection('bodyFunctions')}
          />
        </Field>
        <Field label="Waterworks (Urinary)">
          <Textarea
            value={data.bodyFunctions?.waterworks || ''}
            onChange={v => updateSection('bodyFunctions', 'waterworks', v)}
            onBlur={() => flushSection('bodyFunctions')}
          />
        </Field>
        <Field label="Digestion">
          <Textarea
            value={data.bodyFunctions?.digestion || ''}
            onChange={v => updateSection('bodyFunctions', 'digestion', v)}
            onBlur={() => flushSection('bodyFunctions')}
          />
        </Field>
        <Field label="Appetite">
          <Textarea
            value={data.bodyFunctions?.appetite || ''}
            onChange={v => updateSection('bodyFunctions', 'appetite', v)}
            onBlur={() => flushSection('bodyFunctions')}
          />
        </Field>
        <Field label="Respiratory Function" hint="Ease of breathing, vulnerability to respiratory infections.">
          <Textarea
            value={data.bodyFunctions?.respiratory || ''}
            onChange={v => updateSection('bodyFunctions', 'respiratory', v)}
            onBlur={() => flushSection('bodyFunctions')}
          />
        </Field>
        {showMenstrual && (
          <Field label="Menstrual Cycle" hint="Abnormalities, difficulties; if menopausal, the cycle history.">
            <Textarea
              value={data.bodyFunctions?.menstrual || ''}
              onChange={v => updateSection('bodyFunctions', 'menstrual', v)}
              onBlur={() => flushSection('bodyFunctions')}
            />
          </Field>
        )}
      </Section>

      <Section title="Patient Goals & Engagement" hint="What the patient wants to achieve, and how much responsibility they will accept (F08 B aims 8 and 9).">
        <Field label="Patient's Goals">
          <Textarea
            value={goals}
            onChange={setGoals}
            onBlur={() => onSave({ patientGoals: goals })}
          />
        </Field>
        <Field label="Responsibility Assessment" hint="Practitioner's read on how much the patient will commit to.">
          <Textarea
            value={responsibility}
            onChange={setResponsibility}
            onBlur={() => onSave({ patientResponsibilityNotes: responsibility })}
          />
        </Field>
      </Section>

      <Section title="Practitioner Notes" hint="Working observations not in any of the structured sections.">
        <Field label="Notes">
          <Textarea
            value={practitionerNotes}
            onChange={setPractitionerNotes}
            onBlur={() => onSave({ practitionerNotes })}
            rows={6}
          />
        </Field>
      </Section>
    </div>
  );
};

// ---------- Tiny helpers ----------

const Section: React.FC<{ title: string; hint?: string; children: React.ReactNode }> = ({ title, hint, children }) => (
  <section className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
    <h3 className="text-lg font-semibold text-gray-900 mb-1">{title}</h3>
    {hint && <p className="text-xs text-gray-500 mb-4">{hint}</p>}
    <div className="space-y-4">{children}</div>
  </section>
);

const Field: React.FC<{ label: string; hint?: string; children: React.ReactNode }> = ({ label, hint, children }) => (
  <div>
    <label className="block">
      <span className="text-sm font-medium text-gray-700">{label}</span>
      {hint && <span className="ml-2 text-xs text-gray-500">{hint}</span>}
    </label>
    <div className="mt-1">{children}</div>
  </div>
);

const Textarea: React.FC<{ value: string; onChange: (v: string) => void; onBlur: () => void; rows?: number }> = ({
  value, onChange, onBlur, rows = 4
}) => (
  <textarea
    value={value}
    onChange={e => onChange(e.target.value)}
    onBlur={onBlur}
    rows={rows}
    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-normal focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
  />
);

const SaveIndicator: React.FC<{ status: 'idle' | 'saving' | 'saved' | 'error' }> = ({ status }) => {
  if (status === 'idle') return null;
  const map: Record<string, { text: string; cls: string }> = {
    saving: { text: 'Saving…', cls: 'text-blue-600' },
    saved: { text: '✓ Saved', cls: 'text-green-600' },
    error: { text: '⚠ Save failed', cls: 'text-red-600' }
  };
  const v = map[status];
  return <span className={`text-xs ${v.cls}`}>{v.text}</span>;
};

export default CasesPage;
